import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import toast from "react-hot-toast";
import { Search, Eye } from "lucide-react";

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (search.trim()) params.set("search", search.trim());
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.users);
      setPages(data.pages);
    } catch {
      toast.error("Could not load users.");
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    const t = setTimeout(fetch, 300);
    return () => clearTimeout(t);
  }, [fetch]);

  return (
    <div className="fade-in">
      <div className="admin-page-header">
        <h2>
          Users{" "}
          <span
            className="badge badge-neutral"
            style={{ fontSize: "0.85rem", marginLeft: 8 }}
          >
            {users.length}
          </span>
        </h2>
      </div>

      <div className="admin-controls">
        <div className="admin-search-wrap">
          <Search size={15} className="admin-search-icon" />
          <input
            className="admin-search"
            placeholder="Search by name, email, roll..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Username</th>
              <th>Roll No.</th>
              <th>Sells</th>
              <th>Listings</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 32 }}>
                  <div className="spinner" style={{ margin: "0 auto" }} />
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u._id}>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      {u.avatar ? (
                        <img
                          src={u.avatar}
                          alt=""
                          className="avatar"
                          style={{ width: 32, height: 32 }}
                        />
                      ) : (
                        <div
                          className="avatar-placeholder"
                          style={{ width: 32, height: 32, fontSize: "0.8rem" }}
                        >
                          {u.name?.[0]}
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>
                          {u.name}
                        </div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "var(--text-muted)",
                          }}
                        >
                          {u.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{u.username || "—"}</td>
                  <td>{u.rollNumber || "—"}</td>
                  <td>{u.sellCount ?? 0}</td>
                  <td>{u.activeListings ?? 0}</td>
                  <td>
                    {u.isAdmin ? (
                      <span className="badge badge-info">Admin</span>
                    ) : u.isBlocked ? (
                      <span className="badge badge-danger">Blocked</span>
                    ) : (
                      <span className="badge badge-success">Active</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => navigate(`/admin/users/${u._id}`)}
                    >
                      <Eye size={14} /> View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            marginTop: 24,
          }}
        >
          <button
            className="btn btn-secondary btn-sm"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>
          <span
            style={{
              lineHeight: "36px",
              fontSize: "0.875rem",
              color: "var(--text-muted)",
            }}
          >
            {page} / {pages}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            disabled={page === pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
